import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Button, Col, Flex, Input, Modal, Row } from 'antd';
import { chain, clone, isEmpty, some } from 'lodash';
import { TOrderField } from 'types/orderFields/orderFields';

import { ActionsMenu } from '@atoms/ActionsMenu';
import { SettingsManager } from '@classes/settingsManager';
import RenderIf from '@common/RenderIf';
import { DynamicList } from '@components/common/DynamicList';
import { createAdapter } from '@components/common/DynamicList/types';
import { useFetchDataQuery } from '@hooks/useFetchDataQuery';
import { rootStore } from '@store/rootStore/instanse';
import { apiRequest } from '@utils/apiRequest';

import { MODE_CREATE, MODE_UPDATE, MODE_VIEW } from '../../constants';
import { TPanelContainer } from '@molecules/sidePanelSelect';

import { StyledCell } from './StyledComponents';

/** Сколько места держать под раскрытым списком вариантов — высота попапа antd. */
const DROPDOWN_RESERVE = 260;

import { Field } from './components/Field';
import { useEntriesState } from './hooks/useEntries';

import css from './styles.module.scss';

type Item = {
    id: string;
    name: string;
    status: string;
    field_group_id?: string | null;
    [key: string]: any;
};

/* ------------------------------------------------------------------
 * Константы
 * ----------------------------------------------------------------*/
const REQUIRED_STATUS = 'main';
const DISABLED_STATUS = 'disabled';

/* ------------------------------------------------------------------
 * Адаптер
 * ----------------------------------------------------------------*/
const responseAdapter = createAdapter(({ response }) => {
    const { data } = response.data;
    return {
        name: data.name,
        items: data.fields ?? [],
    };
});

/* ------------------------------------------------------------------
 * Компонент
 * ----------------------------------------------------------------*/

type Props = {
    objectName: string;
    setObjectName: (name: string) => void;

    existingFields?: TOrderField[];
    objId?: number | null;
    categoryId?: number | null;

    customMode?: 'create' | 'update' | 'view' | null;
    /** Границы панели вариантов характеристики; в модалках — сама модалка. */
    panelContainer?: TPanelContainer;

    entityType?: 'baseObject' | 'object' | 'product' | 'baseProduct';

    setExternalValueLabels?: (labels: Record<number, string>) => void;
    setExternalSelectedValues?: (values: Record<number, number>) => void;
    setExternalItemsRef?: (items: any[]) => void;
    setFields?: (items: any[]) => void;
    onAutoSave?: (fields?: any[]) => Promise<void | boolean>;
};

export const Entries = ({
    objectName,
    setObjectName,
    existingFields = [],
    objId = null,
    categoryId,
    customMode = null,
    entityType = 'object',
    setExternalValueLabels = () => {},
    setExternalSelectedValues = () => {},
    setExternalItemsRef = () => {},
    setFields = () => {},
    onAutoSave,
    panelContainer,
}: Props) => {
    const {
        objectId: objectIdFromParams,
        customerId,
        productId,
        entryId: baseProductId,
    } = useParams();
    const productID = productId || baseProductId;
    const isProductEntity = entityType === 'product' || entityType === 'baseProduct';
    const creds = SettingsManager.getConnectionCredentials();
    const objectId = objectIdFromParams ?? objId;
    // объект, уже созданный на сервере (мгновенное создание): id сущности лежит в URL.
    // В модалках (objId-проп, customMode='create') приходит id категории — там сущности ещё нет
    const isPersistedObjectCreate = entityType === 'object' && !!objectIdFromParams;
    const navigator = useNavigate();
    const location = useLocation();

    const { data: fieldCategoriesData } = useFetchDataQuery({
        getFnKey: 'getOrderFieldCategories',
        setDataList(data) {
        },
    });

    const {
        searchTexts,
        customOptions,
        selectedValues,
        fieldIds,
        valueLabels,
        requiredIds,
        openStates,
        getSelectRef,
        handleValueChange,
        handleSearch,
        addCustomOption,
        setOpenStates,
        setValueLabels,
        setSearchTexts,
        setSelectedValues,
        setRequiredIds,
        setCustomOptions,
        setFieldIds,
    } = useEntriesState();

    // Лаконичная навигация: убираем '/create' или '/edit' и добавляем ID
    const navigateToObject = (id: number) => {
        const levelsUp = mode === MODE_CREATE ? 2 : 2;
        const uri = location.pathname.split('/');
        const cuted = uri.splice(0, uri.length - levelsUp);
        cuted.push(id.toString());
        const newPath = cuted.join('/');

        navigator(newPath);
    };

    // Определение режима работы компонента по URI
    const mode = useMemo(() => {
        const pathname = location.pathname;
        const lastSegment = pathname.split('/').pop()?.toLowerCase();

        if (lastSegment === 'create' || customMode === MODE_CREATE) {
            return MODE_CREATE;
        } else if (lastSegment === 'edit') {
            return MODE_UPDATE;
        } else {
            return MODE_VIEW;
        }
    }, [location.pathname, customMode]);

    useEffect(() => {
        if ((entityType === 'product' || entityType === 'baseProduct') && !!setFields) {
            const fullFields = itemsRef.current.map((item) => {
                const selectedValueId = selectedValues[item.id];
                const valueLabel = selectedValueId ? valueLabels[selectedValueId] : undefined;

                return {
                    id: item.id,
                    field_group_id: item.field_group_id,
                    name: item.name,
                    value: {
                        id: selectedValueId,
                        value: valueLabel ?? '',
                    },
                };
            });

            setFields(fullFields);
        }
    }, [entityType, selectedValues, valueLabels, setFields]);

    // Очищаем состояние при смене режима
    useEffect(() => {
        setSelectedValues({});
        setValueLabels({});
        setFieldIds({});
        setRequiredIds([]);
        setCustomOptions({});
        setSearchTexts({});
        setOpenStates({});
        // Полностью очищаем itemsRef при смене режима
        itemsRef.current = [];
        // Сбрасываем отслеживание режима для принудительного обновления
        lastUpdatedModeRef.current = '';
        // Сбрасываем флаг обработки
        isProcessingRef.current = false;
        lastSavedValuesRef.current = {};
        setIsLoading(false);
        // НЕ очищаем кэш - позволяем данным сохраняться между режимами
    }, [mode]);

    /* все загруженные (уже отфильтрованные) поля */
    const itemsRef = useRef<any[]>([]);

    useEffect(() => {
        setExternalItemsRef(itemsRef.current);
    }, [itemsRef.current]);

    useEffect(() => {
        setExternalSelectedValues(selectedValues);
    }, [selectedValues]);

    useEffect(() => {
        setExternalValueLabels(valueLabels);
    }, [valueLabels]);

    // Отслеживаем для какого режима последний раз обновляли itemsRef
    const lastUpdatedModeRef = useRef<string>('');

    // Кэш для API запросов
    const [apiCache, setApiCache] = useState<{
        categoryData?: any;
        objectData?: any;
    }>({});

    // Состояние загрузки
    const [isLoading, setIsLoading] = useState(false);
    const [listReloadKey, setListReloadKey] = useState(0);

    // Ref для защиты от множественных вызовов
    const isProcessingRef = useRef(false);
    const lastSavedValuesRef = useRef<Record<number, number>>({});

    // Рефы для актуальных значений без проблемы stale closure
    const selectedValuesRef = useRef(selectedValues);
    const valueLabelsRef = useRef(valueLabels);
    const fieldIdsRef = useRef(fieldIds);
    selectedValuesRef.current = selectedValues;
    valueLabelsRef.current = valueLabels;
    fieldIdsRef.current = fieldIds;

    // Состояние для модалки добавления нового поля
    const [isAddFieldModalOpen, setIsAddFieldModalOpen] = useState(false);
    const [newFieldName, setNewFieldName] = useState('');

    /* --------------------------------------------------------------
     * Загрузка данных
     * ------------------------------------------------------------*/

    const getApiHandlers = useCallback(() => {
        if (entityType === 'product') {
            return {
                getCategory: (id: number) =>
                    apiRequest((api) =>
                        api.getCRMCategory({ categoryId: id, isWithValues: true, type: 'product', crmId: creds.crmID })
                    ),
                getEntity: (id: number, crmID: number) =>
                    apiRequest((api) => api.getProduct(id, crmID)),
                createEntity: (payload: any) => apiRequest((api) => api.saveProduct(payload)),
                updateEntity: (payload: any) => apiRequest((api) => api.updateProduct(payload)),
            };
        }

        if (entityType === 'baseProduct') {
            return {
                getCategory: (id: number) =>
                    apiRequest((api) =>
                        api.getCRMCategory({ categoryId: id, isWithValues: true, type: 'product', crmId: creds.crmID })
                    ),
                getEntity: (id: number) => apiRequest((api) => api.getAdminBaseProduct(id)),
                createEntity: (payload: any) =>
                    apiRequest((api) => api.createAdminBaseProduct(payload)),
                updateEntity: (payload: any) =>
                    apiRequest((api) => api.updateAdminBaseProduct(payload)),
            };
        }

        if (entityType === 'baseObject') {
            return {
                getCategory: (id: number) =>
                    apiRequest((api) =>
                        api.getCRMCategory({ categoryId: id, isWithValues: true, type: 'object', crmId: creds.crmID })
                    ),
                getEntity: (id: number) => apiRequest((api) => api.getAdminOrderObject(id)),
                createEntity: (payload: any) =>
                    apiRequest((api) => api.createAdminOrderObjects(payload)),
                updateEntity: (payload: any) => apiRequest((api) => api.updateProduct(payload)),
            };
        }

        // object (по умолчанию)
        return {
            getCategory: (id: number) =>
                apiRequest((api) =>
                    api.getCRMCategory({ categoryId: id, isWithValues: true, type: 'object', crmId: creds.crmID })
                ),
            getEntity: (id: number) => apiRequest((api) => api.getOrderObject(id)),
            createEntity: (payload: any) => apiRequest((api) => api.createOrderObject(payload)),
            updateEntity: (payload: any) => apiRequest((api) => api.updateOrderObject(payload)),
        };
    }, [entityType]);

    const loadCategoryData = useCallback(async () => {
        if (apiCache.categoryData) return apiCache.categoryData;

        // Проверка: если categoryId или objectId не определён — не делаем запрос
        const targetId = entityType === 'object' ? objectId : categoryId;
        if (!targetId) {
            console.warn(
                `[Entries] categoryId/objectId отсутствует, пропускаем загрузку категории. entityType=${entityType}`
            );
            return { data: { data: { name: '', fields: [] } } };
        }

        const response = await getApiHandlers().getCategory(+targetId);

        setApiCache((prev) => ({ ...prev, categoryData: response }));
        return response;
    }, [objectId, categoryId, apiCache.categoryData, getApiHandlers, entityType]);

    const loadObjectData = useCallback(async () => {
        if (apiCache.objectData) return apiCache.objectData;

        const response = await getApiHandlers().getEntity(
            entityType === 'product' || entityType === 'baseProduct' ? +productID : +objectId,
            creds.crmID
        );

        const objData = response.data.data;
        existingObjectDataRef.current = objData;

        const categoryId =
            entityType === 'product' || entityType === 'baseProduct'
                ? objData.activity_category_id || objData.category?.id || objData.activityCategory?.id
                : objData.category?.id;

        // без валидного categoryId запрос category уйдёт как .../undefined — пропускаем его
        if (!categoryId) {
            console.warn(
                `[Entries] categoryId отсутствует в ответе getEntity (entityType=${entityType}), загрузка категории пропущена`
            );
            setApiCache((prev) => ({ ...prev, objectData: response }));
            return response;
        }

        const categoryResponse = await getApiHandlers().getCategory(categoryId);

        setApiCache((prev) => ({
            ...prev,
            categoryData: categoryResponse,
            objectData: response,
        }));

        return response;
    }, [objectId, productID, entityType, apiCache.objectData, getApiHandlers]);

    // Основная функция загрузки в зависимости от режима
    const doLoad = useCallback(async () => {
        const api = getApiHandlers();

        if (mode === MODE_VIEW) {
            return await loadObjectData(); // внутри уже дергает api.getEntity
        }

        if (mode === MODE_CREATE && !isPersistedObjectCreate) {
            // защита: categoryId может быть undefined
            if ((entityType === 'product' || entityType === 'baseProduct') && !categoryId) {
                console.warn(
                    '[Entries] Пропущена загрузка: categoryId отсутствует при создании product'
                );
                return { data: { data: { name: '', fields: [] } } };
            }
            return await loadCategoryData();
        }

        // объект в режиме создания уже существует на сервере (создан из модалки списка),
        // поэтому :objectId в URL — id сущности, и грузим её как при редактировании
        if (mode === MODE_UPDATE || (mode === MODE_CREATE && isPersistedObjectCreate)) {
            // сначала тянем сущность, чтобы узнать category.id
            const objectResponse = await api.getEntity(
                entityType === 'object' ? +objectId : +productID,
                creds.crmID
            );
            const objData = objectResponse.data.data;
            existingObjectDataRef.current = objData;

            const categoryId =
                entityType === 'product' || entityType === 'baseProduct'
                    ? objData.activity_category_id || objData.category?.id || objData.activityCategory?.id
                    : objData.category?.id;

            // без валидного categoryId запрос category уйдёт как .../undefined — пропускаем его
            if (!categoryId) {
                console.warn(
                    `[Entries] categoryId отсутствует в ответе getEntity (entityType=${entityType}), загрузка категории пропущена`
                );
                setApiCache((prev) => ({ ...prev, objectData: objectResponse }));
                return objectResponse;
            }

            const categoryResponse = await api.getCategory(categoryId);

            setApiCache((prev) => ({
                ...prev,
                categoryData: categoryResponse,
                objectData: objectResponse,
            }));

            return categoryResponse;
        }

        return Promise.reject(new Error('Unknown mode'));
    }, [mode, objectId, productID, categoryId, entityType, loadObjectData, loadCategoryData, getApiHandlers]);

    // Состояние для хранения загруженных данных объекта
    // Ref для немедленного доступа к данным
    const existingObjectDataRef = useRef(null);

    const user = rootStore.currentUserStore.getUser;

    const doCreate = async () => {
        // Защита от множественных вызовов
        if (isLoading || isProcessingRef.current) {
            return;
        }

        isProcessingRef.current = true;
        setIsLoading(true);

        if (mode === MODE_UPDATE || (mode === MODE_CREATE && isPersistedObjectCreate)) {
            const response = await getApiHandlers().updateEntity({
                ...(entityType === 'product' || entityType === 'baseProduct'
                    ? { product_id: +objectId }
                    : { order_object_id: +objectId }),
                name: objectName,
                fields: itemsRef.current
                    .filter((item) => selectedValues[item.id] !== undefined)
                    .map((item) => {
                        const selectedValueId = selectedValues[item.id];
                        const valueLabel = valueLabels[selectedValueId];
                        return {
                            id: fieldIds[item.id],
                            name: item.name,
                            value: {
                                id: selectedValueId > 1000000000000 ? undefined : selectedValueId,
                                value: String(valueLabel || selectedValueId),
                            },
                        };
                    }),
            });
            const id = (response?.data?.data as any)?.id;
            // сбрасываем кэш: страница просмотра должна получить свежие данные с бэка,
            // а не закэшированное состояние до сохранения (компонент не размонтируется
            // при переходе create/edit -> view)
            setApiCache({});
            existingObjectDataRef.current = null;
            if (id) navigateToObject(id);
        } else {
            const response = await getApiHandlers().createEntity({
                name: objectName,
                ...(entityType === 'product' || entityType === 'baseProduct'
                    ? { product_category_id: +objectId }
                    : { order_object_category_id: +objectId }),
                ownerable_type: `App\\Models\\Api\\V1\\${+customerId ? 'CustomerProfile' : 'User'}`,
                ownerable_id: +customerId || user?.id,
                fields: itemsRef.current
                    .filter((item) => selectedValues[item.id] !== undefined)
                    .map((item) => {
                        const selectedValueId = selectedValues[item.id];
                        const valueLabel = valueLabels[selectedValueId];
                        const selectedValueGroupFieldId = item.field_group_id;
                        return {
                            name: item.name,
                            value: valueLabel || selectedValueId,
                            field_group_id: selectedValueGroupFieldId,
                            order_field_group: selectedValueGroupFieldId,
                        };
                    }),
            });
            const id = (response?.data?.data as any)?.id;
            setApiCache({});
            existingObjectDataRef.current = null;
            if (id) navigateToObject(id);
        }
    };

    
    const handleFieldAutoSave = useCallback(async () => {
        // в режиме создания автосохранение возможно только для сущности,
        // уже созданной на сервере (id в URI): товар или объект
        const canAutoSaveOnCreate =
            mode === MODE_CREATE && ((isProductEntity && !!productID) || isPersistedObjectCreate);
        if (mode !== MODE_UPDATE && !canAutoSaveOnCreate) return;

        const currentValues = selectedValuesRef.current;
        if (isEmpty(currentValues)) return;

        const hasChanges = some(
            currentValues,
            (value, key) => value !== lastSavedValuesRef.current[+key]
        );
        if (!hasChanges) return;

        const isProduct = entityType === 'product' || entityType === 'baseProduct';

        if (onAutoSave) {
            if (isProduct) {
                const currentFields = chain(itemsRef.current)
                    .map((item) => {
                        const selectedValueId = currentValues[item.id];
                        const valueLabel = valueLabelsRef.current[selectedValueId] || '';
                        return {
                            id: fieldIdsRef.current[item.id],
                            name: item.name,
                            value: {
                                id: selectedValueId > 1000000000000 ? undefined : selectedValueId,
                                value: String(valueLabel),
                            },
                        };
                    })
                    .value();
                await onAutoSave(currentFields);
            } else {
                await onAutoSave();
            }
            lastSavedValuesRef.current = clone(currentValues);
            return;
        }

        const entityId = objectId;
        if (!entityId) return;

        const fields = chain(itemsRef.current)
            .filter((item) => currentValues[item.id] !== undefined)
            .map((item) => {
                const selectedValueId = currentValues[item.id];
                const valueLabel = valueLabelsRef.current[selectedValueId] || '';
                return {
                    id: fieldIdsRef.current[item.id],
                    name: item.name,
                    value: {
                        id: selectedValueId > 1000000000000 ? undefined : selectedValueId,
                        value: String(valueLabel),
                    },
                };
            })
            .value();

        try {
            await getApiHandlers().updateEntity({
                order_object_id: +entityId,
                name: objectName,
                fields,
            });
            lastSavedValuesRef.current = clone(currentValues);
        } catch (_) {
            // ошибки обрабатываются на уровне API-слоя
        }
    }, [mode, objectId, entityType, productID, onAutoSave, objectName, getApiHandlers]);

    const responseInspector = (response: any) => {
        const adapted = responseAdapter(response);

        /* убрать disabled + собрать обязательные + карта label-ов */
        // сортируем сразу, как в UI (группа -> статус -> алфавит): из этого же порядка
        // собирается название объекта (первые 1-3 обязательных полей)
        const nextItems = sortByFieldGroupId(adapted.items as Item[]) as any[];
        const nextReqIds = nextItems
            .filter((f: any) => f.status === REQUIRED_STATUS)
            .map((f: any) => f.id);

        const labelMap: Record<number, string> = {};
        nextItems.forEach((f: any) =>
            (f.values || []).forEach((v: any) => (labelMap[v.id] = v.value))
        );

        // Всегда обновляем данные
        itemsRef.current = nextItems;
        lastUpdatedModeRef.current = mode;

        setRequiredIds(nextReqIds);

        setValueLabels((prevLabels) => {
            // Объединяем новые лейблы с существующими
            return { ...prevLabels, ...labelMap };
        });

        // Инициализируем имя объекта только из данных объекта (не из CRM категории)
        if (mode === MODE_VIEW && adapted.name && adapted.name.trim() !== '') {
            setObjectName(adapted.name);
        } else if (
            (mode === MODE_UPDATE || (mode === MODE_CREATE && isPersistedObjectCreate)) &&
            existingObjectDataRef.current &&
            existingObjectDataRef.current.name
        ) {
            setObjectName(existingObjectDataRef.current.name);
        }

        // Используем данные либо из пропсов, либо из загруженного объекта
        // Используем ref для немедленного доступа к данным
        const fieldsToInit =
            existingFields.length > 0
                ? existingFields
                : existingObjectDataRef.current && existingObjectDataRef.current.fields
                ? existingObjectDataRef.current.fields
                : [];

        if (
            (mode === MODE_UPDATE || (mode === MODE_CREATE && isPersistedObjectCreate)) &&
            fieldsToInit.length > 0
        ) {
            const initialValues: Record<number, number> = {};
            const initialLabels: Record<number, string> = {};
            const initialFieldIds: Record<number, number> = {};

            fieldsToInit.forEach((field: any, index: number) => {
                // Находим соответствующее поле по имени
                const matchingItem = nextItems.find((item) => item.name === field.name);

                if (matchingItem) {
                    // Если у сущности сохранён вариант с тем же текстом, что и один
                    // из вариантов категории (matchingItem.values), но с другим id
                    // (например значение раньше добавили как "своё", а потом такой же
                    // текст завели в справочник категории) — используем id из справочника,
                    // иначе в списке будет два визуально одинаковых пункта
                    const canonicalOption = (matchingItem.values || []).find(
                        (v: any) =>
                            String(v.value).trim().toLowerCase() ===
                            String(field.value?.value ?? '')
                                .trim()
                                .toLowerCase()
                    );

                    if (field.value?.id && field.value?.value !== '') {
                        // Приводим id к числу: сущность может вернуть его строкой,
                        // а варианты значений в item.values всегда числовые —
                        // без этого Select не находит совпадение и дублирует опцию
                        const valueId = canonicalOption
                            ? Number(canonicalOption.id)
                            : Number(field.value.id);
                        initialValues[matchingItem.id] = valueId;
                        initialLabels[valueId] = canonicalOption?.value ?? field.value.value;
                    } else if (!field.value?.id && field.value?.value) {
                        if (canonicalOption) {
                            const valueId = Number(canonicalOption.id);
                            initialValues[matchingItem.id] = valueId;
                            initialLabels[valueId] = canonicalOption.value;
                        } else {
                            // Пользовательское значение без ID — создаём временный ID
                            const tempId = Date.now() + Math.random();
                            initialValues[matchingItem.id] = tempId;
                            initialLabels[tempId] = field.value.value;
                        }
                    }
                    // Если value.value пустая строка — не pre-select'им ничего, показываем placeholder
                    initialFieldIds[matchingItem.id] = field.id;
                }
            });

            setSelectedValues((prev) => ({ ...prev, ...initialValues }));
            setValueLabels((prev) => ({ ...prev, ...initialLabels }));
            setFieldIds(initialFieldIds);
        }

        return { ...adapted, items: nextItems };
    };

    /* --------------------------------------------------------------
     * objectName := первые 1–3 полей (placeholder при отсутствии)
     * ------------------------------------------------------------*/
    useEffect(() => {
        // В VIEW и UPDATE, если имя уже есть, не трогаем
        if (mode !== MODE_CREATE && objectName.trim() !== '') return;
        if (!itemsRef.current.length) return;

        const requiredFields = itemsRef.current.filter((f) => requiredIds.includes(f.id));
        // Если нет обязательных полей — всё равно рисуем 3 placeholder’а,
        // иначе рисуем ровно сколько есть (макс. 3)
        const count = requiredFields.length > 0 ? Math.min(3, requiredFields.length) : 3;

        const parts = Array.from({ length: count }, (_, idx) => {
            const f = requiredFields[idx];
            if (!f) return '-';
            const valId = selectedValues[f.id];
            return valId != null ? valueLabels[valId] ?? '-' : '-';
        });

        const newName = parts.join(' / ');
        setObjectName(newName);
    }, [selectedValues, valueLabels, setObjectName, objectName, requiredIds, mode]);

    /* --------------------------------------------------------------
     * canSuccess?  (все обязательные заполнены)
     * ------------------------------------------------------------*/
    const isCanSuccess = useMemo(
        () => requiredIds.every((id) => selectedValues[id] !== undefined),
        [requiredIds, selectedValues]
    );

    const doCancel = () => {
        navigator(-1);
    };

    const doEdit = () => {
        // Переход в режим редактирования
        navigator('edit', { relative: 'path' });
    };

    const handleOpenAddFieldModal = () => {
        setNewFieldName('');
        setIsAddFieldModalOpen(true);
    };

    const handleCloseAddFieldModal = () => {
        setIsAddFieldModalOpen(false);
        setNewFieldName('');
    };

    const handleSaveNewField = async () => {
        const trimmedName = newFieldName.trim();

        const isProduct = entityType === 'product' || entityType === 'baseProduct';
        const entityId = isProduct ? productID : objectId;

        if (!trimmedName || !entityId) return;

        const newField = { id: undefined, name: trimmedName, value: { value: '' } };

        try {
            setIsLoading(true);

            if (isProduct && onAutoSave) {
                const currentFields = itemsRef.current.map((item) => {
                    const selectedValueId = selectedValuesRef.current[item.id];
                    const valueLabel = valueLabelsRef.current[selectedValueId] || '';
                    return {
                        id: fieldIdsRef.current[item.id],
                        name: item.name,
                        value: {
                            id: selectedValueId > 1000000000000 ? undefined : selectedValueId,
                            value: String(valueLabel),
                        },
                    };
                });
                await onAutoSave([...currentFields, newField]);
            } else {
                await getApiHandlers().updateEntity({
                    order_object_id: +entityId,
                    name: objectName,
                    fields: [newField],
                });
            }

            // сбрасываем кэш категории, иначе в режиме создания перезагрузка
            // списка вернёт закэшированные поля без только что добавленного
            setApiCache((prev) => ({ ...prev, categoryData: undefined }));
            setListReloadKey((prev) => prev + 1);
            handleCloseAddFieldModal();
        } finally {
            setIsLoading(false);
        }
    };

    const sortByFieldGroupId = (items: Item[]) => {
        // return [...items].sort((a, b) => {
        //     const aGroup = a.field_group_id ?? 0;
        //     const bGroup = b.field_group_id ?? 0;

        //     if (!aGroup && bGroup) return 1;
        //     if (aGroup && !bGroup) return -1;

        //     if (aGroup < bGroup) return -1;
        //     if (aGroup > bGroup) return 1;

        //     const priority = { main: 2, extra: 1, disabled: 0 };
        //     const aPriority = priority[a.status] ?? -1;
        //     const bPriority = priority[b.status] ?? -1;

        //     if (aPriority > bPriority) return -1;
        //     if (aPriority < bPriority) return 1;

        //     return a.name.localeCompare(b.name);
        // });
        return items;
    };

    /* --------------------------------------------------------------
     * рендер
     * ------------------------------------------------------------*/
    let prevGroupId: string | number | null = '';

    // Список вариантов рендерится внутрь ячейки поля и уходит вниз за карточку:
    // у нижних характеристик его обрезало. Пока список открыт, держим под ним
    // место — карточка становится выше, и он помещается целиком
    const isAnyFieldOpen = Object.values(openStates).some(Boolean);

    return (
        <>
            <Col span={24} className={css.styles}>
                <Flex
                    vertical
                    gap={10}
                    className="bg"
                    style={{ paddingBottom: isAnyFieldOpen ? DROPDOWN_RESERVE : undefined }}
                >
                    <DynamicList
                        key={`entries-${objectId || categoryId}-${mode}-${listReloadKey}`}
                        loadData={doLoad}
                        resetDeps={[objectId]}
                        responseAdapter={(response: any) => {
                            const res = responseInspector(response);
                            const items = sortByFieldGroupId(res.items);
                            return { ...res, items };
                        }}
                        pageSize={10}
                        renderItem={(item: any, index: number, allItems: any[]) => {
                            const groupChanged = item.field_group_id !== prevGroupId;
                            prevGroupId = item.field_group_id;

                            return (
                                <React.Fragment key={item.id}>
                                    {groupChanged && (
                                        <div
                                            style={{
                                                fontWeight: 600,
                                                marginTop: index === 0 ? 0 : 16,
                                                marginBottom: 8,
                                                fontSize: 15,
                                                color: '#1677ff',
                                                borderBottom: '1px solid #eaeaea',
                                                paddingBottom: 4,
                                            }}
                                        >
                                            {item.field_group_id
                                                ? fieldCategoriesData?.find(
                                                      (group) => group.id === item.field_group_id
                                                  )?.name || `Группа ${item.field_group_id}`
                                                : 'Прочее'}
                                        </div>
                                    )}

                                    <Row style={{ marginBlock: 10 }}>
                                        <Col span={10}>
                                            <StyledCell>
                                                {(mode === MODE_CREATE || mode === MODE_UPDATE) &&
                                                    item.status === REQUIRED_STATUS && (
                                                        <span
                                                            style={{ color: 'red', marginRight: 4 }}
                                                        >
                                                            *
                                                        </span>
                                                    )}
                                                {item.name}
                                            </StyledCell>
                                        </Col>
                                        <Col span={14}>
                                            <StyledCell>
                                                <Field
                                                    item={item}
                                                    mode={mode}
                                                    selectedValues={selectedValues}
                                                    valueLabels={valueLabels}
                                                    customOptions={customOptions}
                                                    searchTexts={searchTexts}
                                                    openStates={openStates}
                                                    setOpenStates={setOpenStates}
                                                    setValueLabels={setValueLabels}
                                                    setSearchTexts={setSearchTexts}
                                                    getSelectRef={getSelectRef}
                                                    handleSearch={handleSearch}
                                                    handleValueChange={handleValueChange}
                                                    addCustomOption={addCustomOption}
                                                    onFieldBlur={handleFieldAutoSave}
                                                    panelContainer={panelContainer}
                                                />
                                            </StyledCell>
                                        </Col>
                                    </Row>
                                </React.Fragment>
                            );
                        }}
                    />
                    {(mode === MODE_UPDATE ||
                        (mode === MODE_CREATE &&
                            ((isProductEntity && !!productID) || isPersistedObjectCreate))) && (
                        <Button
                            type="dashed"
                            onClick={handleOpenAddFieldModal}
                            style={{ marginTop: 16, width: '100%' }}
                        >
                            Добавить новое поле
                        </Button>
                    )}
                </Flex>
            </Col>

            <RenderIf
                condition={!objId && !(entityType === 'product' || entityType === 'baseProduct')}
            >
                <ActionsMenu
                    doCreate={
                        mode === MODE_CREATE && !isPersistedObjectCreate && isCanSuccess
                            ? doCreate
                            : null
                    }
                    doSave={
                        // объект уже существует на сервере — сохранить можно в любой момент,
                        // обязательность полей кнопку не блокирует (как в товарах)
                        mode === MODE_UPDATE ||
                        (mode === MODE_CREATE && isPersistedObjectCreate)
                            ? doCreate
                            : null
                    }
                    doEdit={mode === MODE_VIEW && entityType === 'object' ? doEdit : null}
                    doCancel={mode !== MODE_VIEW ? doCancel : null}
                />
            </RenderIf>

            <Modal
                title="Добавить новое поле"
                open={isAddFieldModalOpen}
                onOk={handleSaveNewField}
                onCancel={handleCloseAddFieldModal}
                okText="Сохранить"
                cancelText="Отмена"
                centered
            >
                <Input
                    placeholder="Введите название поля"
                    value={newFieldName}
                    onChange={(e) => setNewFieldName(e.target.value)}
                    onPressEnter={handleSaveNewField}
                />
            </Modal>
        </>
    );
};
